import { getParamsFromNode, sendClick } from '@linecorp/dosi-analytics';
import classNames from 'classnames';
import i18next from 'i18next';
import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container, List } from 'yoso';
import { LoginButton } from '~/components/Auth';
import { NotificationButton } from '~/components/Dosi/Notification/Button/NotificationButton';
import { RemoteSearchMode, useRemoteSearchComponent } from '~/components/Dosi/Remote/Remote.hooks';
import { ProfileDropdown } from '~/components/Header/Dropdown/ProfileDropdown';
import { useHeaderTheme, useTransition } from '~/components/Header/Header.hooks';
import { GlobalNavbar } from '~/components/Header/Nav';
import { Translation } from '~/components/I18n';
import { Logo } from '~/components/Icon';
import { DosiLogo } from '~/components/Icon/DosiLogo';
import { useMember } from '~/components/Member/Member.hooks';
import { SidebarButton } from '~/components/Sidebar/Button/SidebarButton';
import styles from './Header.module.scss';

export const Header = () => {
  const ref = useRef<HTMLDivElement>(null);

  const [searchMode, setSearchMode] = useState<RemoteSearchMode>('closed');
  const {
    data: { isLoggedIn },
  } = useMember();

  const { dark, blur } = useHeaderTheme();
  const transition = useTransition();

  const handleModeChange = useCallback((mode: RemoteSearchMode, previousMode: RemoteSearchMode) => {
    if (previousMode === 'closed' && ref.current) {
      const params = getParamsFromNode(ref.current, 'event');
      sendClick('gnb_search', params);
    }

    setSearchMode(mode);
  }, []);

  return (
    <div
      ref={ref}
      className={classNames('header', styles.root, styles[searchMode], {
        [styles.dark]: dark,
        [styles.blur]: blur,
        [styles.transition]: transition,
      })}>
      <div className={styles.inner}>
        <Link to="/" data-event="click" data-event-target="gnb_logo">
          <Logo className={styles.logo} display="block" />
        </Link>
        <GlobalNavbar className={classNames(styles.globalNavBar, 'd-none d-large-block')} />
        <List className={styles.right}>
          <li className="d-block d-large-none">
            <a
              className={styles.link}
              data-event="click"
              data-event-target="gnb_dosi"
              href={import.meta.env.VITE_DOSI_URL}>
              <DosiLogo />
            </a>
          </li>
          <li
            className={styles.remoteSearchListItem}
            ref={useRemoteSearchComponent({
              lang: i18next.resolvedLanguage,
              brandId: 1,
              className: styles.remoteSearch,
              onModeChange: handleModeChange,
            })}
          />
          <li className="d-none d-large-block">
            <a
              className={styles.link}
              data-event="click"
              data-event-target="gnb_dosi"
              href={import.meta.env.VITE_DOSI_URL}>
              <DosiLogo />
            </a>
          </li>
          {isLoggedIn && (
            <li>
              <NotificationButton
                className={styles.button}
                data-event="click"
                data-event-target="gnb_noti"
              />
            </li>
          )}
          <li className="d-none d-large-block">
            {isLoggedIn ? (
              <ProfileDropdown className={classNames(styles.dropdown, styles.profileDropdown)} />
            ) : (
              <LoginButton
                className={styles.loginButton}
                data-event="click"
                data-event-target="login">
                <Translation name="citizen_login_btn" />
              </LoginButton>
            )}
          </li>
          <li className="d-block d-large-none">
            <SidebarButton
              variant="text"
              className={styles.sidebarButton}
              data-event="click"
              data-event-target="sidebar_menu"
            />
          </li>
        </List>
      </div>
      <Container id="header-portal" margin="auto" />
    </div>
  );
};
